import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
  computed,
  inject,
  ElementRef,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Overlay, OverlayRef, OverlayModule, FlexibleConnectedPositionStrategy, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
// ZagIcon import intentionally not included - not needed for color picker

/**
 * Predefined color option
 */
export interface ColorOption {
  name: string;
  label: string;
  value: string;
}

/**
 * Default color palette for entity appearance
 */
export const DEFAULT_COLOR_PALETTE: ColorOption[] = [
  // Blues
  { name: 'blue', label: 'Blue', value: '#3b82f6' },
  { name: 'sky', label: 'Sky', value: '#0ea5e9' },
  { name: 'cyan', label: 'Cyan', value: '#06b6d4' },
  { name: 'teal', label: 'Teal', value: '#14b8a6' },

  // Greens
  { name: 'green', label: 'Green', value: '#22c55e' },
  { name: 'emerald', label: 'Emerald', value: '#10b981' },
  { name: 'lime', label: 'Lime', value: '#84cc16' },

  // Warm colors
  { name: 'yellow', label: 'Yellow', value: '#eab308' },
  { name: 'amber', label: 'Amber', value: '#f59e0b' },
  { name: 'orange', label: 'Orange', value: '#f97316' },
  { name: 'red', label: 'Red', value: '#ef4444' },
  { name: 'rose', label: 'Rose', value: '#f43f5e' },

  // Purples
  { name: 'pink', label: 'Pink', value: '#ec4899' },
  { name: 'fuchsia', label: 'Fuchsia', value: '#d946ef' },
  { name: 'purple', label: 'Purple', value: '#a855f7' },
  { name: 'violet', label: 'Violet', value: '#8b5cf6' },
  { name: 'indigo', label: 'Indigo', value: '#6366f1' },

  // Neutrals
  { name: 'slate', label: 'Slate', value: '#64748b' },
  { name: 'gray', label: 'Gray', value: '#6b7280' },
  { name: 'zinc', label: 'Zinc', value: '#71717a' },
];

/**
 * Color picker component for selecting a color from a palette.
 *
 * @example
 * ```html
 * <zag-color-picker
 *   [placeholder]="'Select color'"
 *   [(ngModel)]="selectedColor"
 * />
 * ```
 */
@Component({
  selector: 'zag-color-picker',
  templateUrl: './color-picker.html',
  styleUrl: './color-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-color-picker',
    '[class.zag-color-picker--open]': 'isOpen()',
    '[class.zag-color-picker--disabled]': 'disabled()',
    '[class.zag-color-picker--focused]': 'focused()',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class ColorPicker implements ControlValueAccessor, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @ViewChild('panelTemplate') panelTemplate!: TemplateRef<unknown>;

  /** Placeholder text when no color is selected */
  readonly placeholder = input<string>('Select a color');

  /** Whether the picker is disabled */
  readonly disabled = input<boolean>(false);

  /** Whether the picker is clearable */
  readonly clearable = input<boolean>(true);

  /** Custom color palette (defaults to DEFAULT_COLOR_PALETTE) */
  readonly colors = input<ColorOption[]>(DEFAULT_COLOR_PALETTE);

  /** Whether to allow custom hex color input */
  readonly allowCustom = input<boolean>(true);

  /** Internal state */
  protected readonly isOpen = signal<boolean>(false);
  protected readonly focused = signal<boolean>(false);
  protected readonly customColorInput = signal<string>('');
  private readonly selectedValue = signal<string | null>(null);

  /** Overlay reference */
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;

  /** Selected color from the palette */
  readonly selectedColor = computed(() => {
    const value = this.selectedValue();
    if (!value) return null;
    return (
      this.colors().find(c => c.value === value) ?? {
        name: 'custom',
        label: 'Custom',
        value,
      }
    );
  });

  /** Computed: Display text for the trigger */
  readonly displayText = computed(() => {
    const color = this.selectedColor();
    return color ? color.label : this.placeholder();
  });

  /** Whether to show clear button */
  readonly showClearButton = computed(() => {
    return this.clearable() && this.selectedValue() !== null && !this.disabled();
  });

  /** Whether the custom color input is valid */
  readonly isCustomColorValid = computed(() => {
    const input = this.customColorInput();
    return /^#[0-9A-Fa-f]{6}$/.test(input);
  });

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnDestroy(): void {
    this.destroyOverlay();
  }

  writeValue(value: string | null): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // Managed via input signal
  }

  /** Toggle picker open state */
  toggle(): void {
    if (this.disabled()) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Open the picker */
  open(): void {
    if (this.disabled() || this.isOpen()) return;
    this.isOpen.set(true);
    this.focused.set(true);
    this.customColorInput.set('');
    this.createOverlay();
  }

  /** Close the picker */
  close(): void {
    this.isOpen.set(false);
    this.destroyOverlay();
  }

  /** Select a color */
  selectColor(color: ColorOption): void {
    this.selectedValue.set(color.value);
    this.onChange(color.value);
    this.close();
  }

  /** Select a custom color */
  selectCustomColor(): void {
    const value = this.customColorInput();
    if (this.isCustomColorValid()) {
      this.selectedValue.set(value);
      this.onChange(value);
      this.close();
    }
  }

  /** Clear the selection */
  clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectedValue.set(null);
    this.onChange(null);
  }

  /** Handle custom color input */
  onCustomColorInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Auto-prepend # if not present
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }

    this.customColorInput.set(value);
  }

  /** Handle focus */
  onTriggerFocus(): void {
    this.focused.set(true);
  }

  /** Handle blur */
  onTriggerBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  /** Handle keyboard events */
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (!this.isOpen()) {
        event.preventDefault();
        this.open();
      }
    }
  }

  /** Check if a color is selected */
  isSelected(color: ColorOption): boolean {
    return this.selectedValue() === color.value;
  }

  private createOverlay(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.getPositionStrategy();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: 280,
      maxHeight: 400,
    });

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      if (this.elementRef.nativeElement.contains(event.target as Node)) {
        return;
      }
      this.close();
    });

    this.overlayRef.detachments().subscribe(() => {
      this.close();
    });

    this.portal = new TemplatePortal(this.panelTemplate, this.viewContainerRef);
    this.overlayRef.attach(this.portal);
  }

  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 4,
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -4,
      },
    ];

    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false);
  }

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.portal = null;
  }
}
